import React, { useCallback, useRef, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import SedaScatterplotBase from './SedaScatterplotBase'
import clsx from 'clsx'
import useResizeAware from 'react-resize-aware'
import {
	useAppContext,
	useDemographic,
	useMetric,
	useSecondary,
	useUnit,
	useUiStore
} from '../../app/hooks'
import {
	getDemographicIdFromVarName,
	getDemographicForVarNames,
	getSecondaryVarsForDemographic,
	getSecondaryVarsForGaps,
	getMetricIdFromVarName,
	getMetricUnitDefault,
	getMetricUnits,
	isGapDemographic,
} from '../../app/selectors'
import { getPrefixLang } from '../../app/selectors/lang'
import {
	getKeyMetrics,
} from '../../app/selectors/metrics'
import { LinkButton, SplitView } from '../../../../shared'
import SelectMenu from '../../../../shared/components/Inputs/SelectMenu'
import {
	getLocatonIdFromEvent,
	getCoordsFromEvent,
	isMarkerRelated
} from '../utils'
import { useAddLocation } from '../../location'
import SedaScatterplotHeader from './SedaScatterplotHeader'
import SedaScatterplotAxisButton from './SedaScatterplotAxisButton'
import shallow from 'zustand/shallow'

/** Breakpoint where gap chart is split vs overlay */
const SPLIT_BREAKPOINT = 1024

const useStyles = makeStyles(theme => ({
	root: {
		position: 'relative',
		width: '100%',
		height: '100%',
		userSelect: 'none'
	},
	chart: {
		position: 'absolute',
		top: theme.spacing(12),
		left: theme.spacing(4),
		right: theme.spacing(8),
		bottom: theme.spacing(6),
		[theme.breakpoints.down('sm')]: {
			left: theme.spacing(1),
			right: theme.spacing(6)
		},
		'& .scatterplot-axis .MuiTypography-root.MuiTypography-body2': {
			fontSize: theme.typography.pxToRem(14)
		}
	},
	// make space for toggle button
	headerOffset: {
		[theme.breakpoints.up('sm')]: {
			right: 88
		}
	},
	toggleButton: {
		marginLeft: 0,
		minWidth: 80,
		maxWidth: 88,
		whiteSpace: 'normal',
		fontSize: theme.typography.pxToRem(12),
		position: 'absolute',
		top: theme.spacing(-8),
		right: -32,
		[theme.breakpoints.up('md')]: {
			position: 'absolute',
			top: -1,
			right: -88
		}
	}
}))

const SedaScatterplot = () => {
	// ref to track the timeout that clears the tooltip
	const timeoutRef = useRef(null)

	// track size of chart area
	const [resizeListener, sizes] = useResizeAware()

	// pull required items from the app context
	const {
		region,
		isVersus,
		hasGapChart,
		allData,
		scatterplotData,
		scatterplotVars,
		gapVars,
		gapExtents,
		scatterplotExtents,
		colorExtent,
	} = useAppContext()
	const [xVar, yVar] = scatterplotVars
	const [xGapVar, yGapVar] = gapVars
	const [metric, setMetric] = useMetric()
	const [secondary, setSecondary] = useSecondary()
	const [unit, setUnit] = useUnit()
	const [demographic] = useDemographic()
	const [setHovered, isEmbed, embedSecondary] = useUiStore(
		state => [
			state.setHovered,
			state.isEmbed,
			state.embedSecondary
		],
		shallow
	)
	const addLocation = useAddLocation()
	const metricUnits = getMetricUnits(metric)
	const defaultUnit = getMetricUnitDefault(metric)
	const metrics = getKeyMetrics().map(m => m.id)
	// track state for split view of charts
	const [showGapChart, setShowGapChart] = useState(false)
	const isGap = isGapDemographic(demographic)
	const hasUnits = metricUnits && metricUnits.length > 0

	const isSplit =
		!isEmbed && sizes && sizes.width > SPLIT_BREAKPOINT

	const xDemId = getDemographicIdFromVarName(xVar)
	const yDemId = getDemographicIdFromVarName(yVar)
	const gapId = getDemographicForVarNames(yVar, xVar)

	// console.log({xVar,yVar,xGapVar,yGapVar,metric,secondary,unit,allData,scatterplotData})
	// flip label order for school poverty metric
	const flipLabelOrder =
		xVar.indexOf('seg') > -1 || xVar.indexOf('min') > -1

	const labelPrefix =
		isEmbed
			? 'LABEL'
			: hasGapChart
				? flipLabelOrder
					? 'LABEL_PRE_FLIP'
					: 'LABEL_PRE'
				: 'LABEL_PRE'


	const xAxisLabelLeft = isGap || isEmbed ? getPrefixLang(isEmbed ? xVar : xDemId, labelPrefix) : ""
	const xAxisLabelRight = isGap && !isEmbed ? "" : getPrefixLang(isEmbed ? xGapVar : gapId, labelPrefix)

	const yAxisLabelLeft = getPrefixLang(isEmbed ? yVar : yDemId, labelPrefix)
	const yAxisLabelRight = getPrefixLang(isEmbed ? yGapVar : gapId, labelPrefix)

	const demVarNames = getSecondaryVarsForDemographic(demographic)
	const gapVarNames = getSecondaryVarsForGaps(gapId)

	const secondaryVarNames = isGap ? gapVarNames : demVarNames

	const xAxisChildrenLeft = (
		<>
			{!hasGapChart && !isEmbed &&
				<SelectMenu
					style={{
						position: 'relative',
						zIndex: 1000,
						display: 'inline-block',
						marginRight: 10
					}}
					// match selected secondary with item if it specifies dem or gap
					value={secondaryVarNames.find(s => s.includes(secondary))}
					items={secondaryVarNames}
					onSelect={s => {
						// parse out secondary metric ID if specified in items
						setSecondary(getMetricIdFromVarName(s) || s)
					}}
					ButtonComponent={SedaScatterplotAxisButton}
				/>
			}
			{hasGapChart && !isEmbed &&
				<>
					<SelectMenu
						style={{
							position: 'relative',
							zIndex: 1000,
							display: 'inline-block',
							marginRight: 10
						}}
						value={metric}
						items={metrics}
						onSelect={m => setMetric(m)}
						ButtonComponent={SedaScatterplotAxisButton}
					/>
					{hasUnits &&
						<>
							<span>Shown As&nbsp;&nbsp;</span>
							<SelectMenu
								style={{
									position: 'relative',
									zIndex: 1000,
									display: 'inline-block',
									// marginRight: 4
								}}
								value={unit}
								items={metricUnits}
								// label={getPrefixLang(unit, 'axis')}
								onSelect={u => setUnit(u)}
								ButtonComponent={SedaScatterplotAxisButton}
							/>
						</>
					}
				</>
			}
		</>
	)

	const yAxisChildrenLeft = (
		<>
			{!isEmbed &&
				<SelectMenu
					style={{
						position: 'relative',
						zIndex: 1000,
						display: 'inline-block',
						marginRight: 10
					}}
					value={metric}
					items={metrics}
					onSelect={m => setMetric(m)}
					ButtonComponent={SedaScatterplotAxisButton}
				/>
			}
			{hasUnits &&
				<>
					<span>Shown As&nbsp;&nbsp;</span>
					<SelectMenu
						style={{
							position: 'relative',
							zIndex: 1000,
							display: 'inline-block',
							// marginRight: 4
						}}
						value={unit}
						items={metricUnits}
						// label={getPrefixLang(unit, 'axis')}
						onSelect={u => setUnit(u)}
						ButtonComponent={SedaScatterplotAxisButton}
					/>
				</>
			}
		</>
	)

	const xAxisChildrenRight = (
		!isEmbed &&
			<SelectMenu
				style={{
					position: 'relative',
					zIndex: 1000,
					display: 'inline-block',
					marginRight: 4
				}}
				// value={secondary}
				// items={gapMetrics}
				// onSelect={m => setSecondary(m)}
				// match selected secondary with item if it specifies dem or gap
				value={secondaryVarNames.find(s => s.includes(secondary))}
				items={secondaryVarNames}
				onSelect={s => {
					// parse out secondary metric ID if specified in items
					setSecondary(getMetricIdFromVarName(s) || s)
				}}
				ButtonComponent={SedaScatterplotAxisButton}
			/>
	)

	const yAxisChildrenRight = (
		<>
			{!isEmbed &&
				<SelectMenu
					style={{
						position: 'relative',
						zIndex: 1000,
						display: 'inline-block',
						marginRight: 10
					}}
					value={metric}
					items={metrics}
					onSelect={m => setMetric(m)}
					ButtonComponent={SedaScatterplotAxisButton}
				/>
			}
			{hasUnits &&
				<>
					<span>Shown As&nbsp;&nbsp;</span>
					<SelectMenu
						style={{
							position: 'relative',
							zIndex: 1000,
							display: 'inline-block',
							// marginRight: 4
						}}
						value={unit}
						items={metricUnits}
						// label={getPrefixLang(unit, 'axis')}
						onSelect={u => setUnit(u)}
						ButtonComponent={SedaScatterplotAxisButton}
					/>
				</>
			}
		</>
	)


	// handle hover events
	const handleHover = useCallback(
		(e, eChartInstance, vars) => {
			// grab data from event
			const id = getLocatonIdFromEvent(e)
			const coords = getCoordsFromEvent(e)
			if (e.type === 'mouseover') {
				// set the current hovered ID and coords
				setHovered(id, coords, {
					showTooltip: true,
					showMarkers: true,
					forceVars: [vars[1], vars[0]] // y and x vars from chart
				})
				// if the tooltip is going to be cleared, cancel it
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
					timeoutRef.current = null
				}
			}
			// set a timeout to clear the tooltip
			if (e.type === 'mouseout' && !isMarkerRelated(e)) {
				if (timeoutRef.current) clearTimeout(timeoutRef.current)
				timeoutRef.current = setTimeout(
					() => setHovered(null),
					400
				)
			}
		},
		[setHovered]
	)

	// handle click events
	const handleClick = useCallback(
		e => {
			// grab data from event
			const id = getLocatonIdFromEvent(e)
			addLocation(id)
		},
		[addLocation]
	)
	const classes = useStyles()

	// if exists, set default unit as active
	useEffect(() => {
		setUnit((unit || defaultUnit))
	}, [defaultUnit])

	return (
		<div className={clsx(classes.root)}>
			{resizeListener}
			<SplitView
				LeftComponent={
					isEmbed && embedSecondary ? (
						<></>
					) : (
						<SedaScatterplotBase
							className={clsx(classes.chart, {
								'scatterplot--versus': isVersus
							})}
							variant="map"
							data={scatterplotData}
							allData={allData}
							vars={scatterplotVars}
							extents={scatterplotExtents}
							colorExtent={colorExtent}
							region={region}
							xAxisLabel={xAxisLabelLeft}
							yAxisLabel={yAxisLabelLeft}
							xAxisChildren={xAxisChildrenLeft}
							yAxisChildren={yAxisChildrenLeft}
							onHover={handleHover}
							onClick={handleClick}>
							<SedaScatterplotHeader
								className={clsx({
									[classes.headerOffset]: !isSplit && hasGapChart
								})}
								xVar={xVar}
								yVar={yVar}
								region={region}
								highlighted={scatterplotData.length}
								total={allData.length}>
								{!isSplit && hasGapChart && !isEmbed && (
									<LinkButton
										className={classes.toggleButton}
										onClick={() => setShowGapChart(true)}>
										Show gap vs. other metrics
									</LinkButton>
								)}
							</SedaScatterplotHeader>
						</SedaScatterplotBase>
					)
				}
				RightComponent={
					hasGapChart ? (
						<SedaScatterplotBase
							className={clsx('scatterplot--gap', classes.chart)}
							// gapChart
							variant="map"
							vars={gapVars}
							extents={gapExtents}
							data={scatterplotData}
							allData={allData}
							colorExtent={colorExtent}
							region={region}
							xAxisLabel={xAxisLabelRight}
							yAxisLabel={yAxisLabelRight}
							xAxisChildren={xAxisChildrenRight}
							yAxisChildren={yAxisChildrenRight}
							onHover={handleHover}
							onClick={handleClick}>
							<SedaScatterplotHeader
								className={clsx({
									[classes.headerOffset]: !isSplit && isVersus
								})}
								xVar={xGapVar}
								yVar={yGapVar}
								region={region}
								highlighted={scatterplotData.length}
								total={allData.length}>
								{!isSplit && isVersus && !isEmbed && (
									<LinkButton
										className={classes.toggleButton}
										onClick={() => setShowGapChart(false)}>
										Show Versus
									</LinkButton>
								)}
							</SedaScatterplotHeader>
						</SedaScatterplotBase>
					) : (
						<></>
					)
				}
				view={
					isSplit && hasGapChart
						? 'split'
						: (showGapChart || embedSecondary) && hasGapChart
						? 'right'
						: 'left'
				}
				style={{
					width: '100%',
					height: '100%'
				}}
			/>
		</div>
	)
}

export default SedaScatterplot
